/** SAVED SCREENS — filtered universes someone kept. */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_MY_CUSTOM_SCREENS } from '../queries';
import { DELETE_SCREEN, SAVE_SCREEN } from '../mutations';
import { EMPTY } from './_shared';

/** What a screen screens, in the words the register uses. */
export const SCREEN_TYPE_LABEL = {
  MUTUAL_FUND: 'Mutual fund',
  MODEL: 'Model',
  STOCK: 'Stock',
  ETF: 'ETF',
};

/** The order they are shown in — schemes first, as everywhere else here. */
export const SCREEN_TYPE_ORDER = ['MUTUAL_FUND', 'MODEL', 'STOCK', 'ETF'];

/**
 * The signed-in user's saved screens.
 *
 * Deleting re-reads the list rather than patching it: the mutation answers
 * `{ success, message, screenId }` and not the remaining rows, so the list is
 * the only thing that knows what is left.
 */
export function useCustomScreens({ screenType = null, skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_MY_CUSTOM_SCREENS, {
    /* `urlId` null is the listing reading of the same document — the one that
       names a handle returns that single unsaved builder screen instead. */
    variables: { screenType, urlId: null },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [removeMutation, { loading: removing }] = useMutation(DELETE_SCREEN);
  const [saveMutation, { loading: saving }] = useMutation(SAVE_SCREEN);

  const rows = data?.getMyCustomScreens ?? EMPTY;

  const byType = useMemo(() => {
    const out = new Map();
    rows.forEach((s) => {
      const key = s.screenType || 'OTHER';
      out.set(key, [...(out.get(key) || []), s]);
    });
    return out;
  }, [rows]);

  const remove = useCallback(async (screenId) => {
    const res = await removeMutation({ variables: { screenId: Number(screenId) } });
    await refetch();
    return res?.data?.deleteScreen ?? null;
  }, [removeMutation, refetch]);

  /**
   * Keep a screen that has already been RUN, by the handle it was run under.
   *
   * There is no "new screen" write: the server rehydrates the result set it
   * stored under `urlId` and persists it as-is, so a screen is created by
   * running the screener and naming the answer. The handle lives for an hour,
   * which is why this reports the server's own message rather than assuming it
   * worked — an expired handle fails, and the reader has to be told to run it
   * again rather than left looking at a list that never grew.
   */
  const save = useCallback(async (name, urlId) => {
    const res = await saveMutation({ variables: { name: String(name).trim(), urlId: String(urlId) } });
    await refetch();
    return res?.data?.saveScreen ?? null;
  }, [saveMutation, refetch]);

  return useMemo(
    () => ({ rows, byType, loading, error, refetch, remove, save, busy: removing || saving }),
    [rows, byType, loading, error, refetch, remove, save, removing, saving],
  );
}

/** A saved screen's id is a number; a run's handle (`urlId`) is anything else. */
export const isScreenHandle = (v) => v != null && String(v).trim() !== '' && !/^\d+$/.test(String(v).trim());

/**
 * One screen by the handle the screener tool ran it under (`urlId`) — a run
 * that may not be saved yet. The agent's screener answers with that handle;
 * navigating to the Screener with it shows the run, and "Save screen" keeps
 * it. The handle lives an hour on the server, so an expired one answers
 * empty and the screen says to run it again.
 */
export function useScreenByHandle(urlId, { skip = false } = {}) {
  const handle = isScreenHandle(urlId) ? String(urlId).trim() : null;
  const { data, loading, error } = useQuery(GET_MY_CUSTOM_SCREENS, {
    variables: { screenType: null, urlId: handle },
    skip: skip || !handle,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const row = handle ? (data?.getMyCustomScreens ?? EMPTY)[0] ?? null : null;
    /* A run not yet saved comes back with no id (0 or null); a saved screen's id is a positive number. */
    return { handle, screen: row ? { ...row, urlId: handle, unsaved: !(Number(row.id) > 0) } : null, loading, error };
  }, [handle, data, loading, error]);
}
